import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { readCart, invalidateCartRead } from '@theme/utilities';

const COUNTDOWN_EVENT_SOURCE = 'cart-gift-countdown';
const COUNTDOWN_DURATION_MS = 59 * 60 * 1000;
const CHECKOUT_SYNC_TIMEOUT_MS = 8000;
const CHECKOUT_PAUSE_STORAGE_KEY = 'organics-gift-checkout-pause';
const cartResourceVersions = new WeakMap();
const CART_ATTRIBUTE_KEYS = {
  enabled: '_on_gift_timer_enabled',
  deadline: '_on_gift_deadline',
  state: '_on_gift_state',
  count: '_on_gift_qualifying_count',
  items: '_on_gift_qualifying_items',
  available: '_on_gift_offer_active',
  remaining: '_on_gift_remaining_ms',
};

const isCountdownEnabled = document.querySelector('[data-gift-countdown-enabled]')?.dataset.giftCountdownEnabled === 'true';

async function requestCartResource(url, options = {}) {
  const response = await fetch(url, { ...options, signal: options.signal || AbortSignal.timeout(10000) });
  const cart = await response.json();
  if (!response.ok) {
    const error = new Error(cart.description || 'Could not update the gift offer');
    error.status = response.status;
    throw error;
  }
  if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
  const responseVersion = cartResourceVersions.get(response);
  if (responseVersion !== undefined) cartResourceVersions.set(cart, responseVersion);
  return cart;
}

function getPaidItemsSignature(cart) {
  return JSON.stringify(cart.items
    .filter((item) => String(item.properties?._free_product) !== 'true')
    .map((item) => [item.key, item.quantity, item.selling_plan_allocation?.selling_plan?.id || 0])
    .sort((first, second) => String(first[0]).localeCompare(String(second[0]))));
}

class KiteGiftRules {
  #hasReportedCompatibilityError = false;

  reportCompatibilityError() {
    if (this.#hasReportedCompatibilityError) return;
    this.#hasReportedCompatibilityError = true;
    console.warn('[cart-gift-countdown] Kite integration is unavailable. Gift eligibility cannot be confirmed.');
  }

  getEngine() {
    const engine = window.gfg?.gfgFreeGiftV2;
    if (!engine || engine._isStub) return null;
    if (!Array.isArray(engine.state?.campaigns) ||
        typeof engine.rules?.evaluateAll !== 'function' ||
        typeof engine.rules?.evaluate !== 'function' ||
        typeof engine.eligibility?.check !== 'function' ||
        typeof engine.eligibility?._checkCartEligibility !== 'function' ||
        typeof engine.cart?._buildDesiredState !== 'function' ||
        typeof engine.cart?.syncGiftsWithCart !== 'function' ||
        typeof engine._processCartChange !== 'function' ||
        typeof window.gfg?.utility?.addToCartV2 !== 'function' ||
        typeof window.gfg?.utility?.updateCache !== 'function') {
      this.reportCompatibilityError();
      return null;
    }
    return engine;
  }

  getOffer(cart) {
    const engine = this.getEngine();
    if (!engine || engine.state.isProcessing || !engine.state.campaigns.length) return null;
    try {
      const campaigns = [];
      const gifts = [];
      for (const [campaignIndex, campaign] of engine.state.campaigns.entries()) {
        if (typeof campaign?.isEnabled !== 'boolean') throw new Error('Unsupported Kite campaign');
        if (!campaign.isEnabled) continue;
        if (campaign.isGBPWidget) throw new Error('Unsupported Kite campaign');
        if (!engine.eligibility.check(campaign)?.eligible ||
            !engine.eligibility._checkCartEligibility(campaign, cart)) continue;
        const results = engine.rules.evaluateAll(campaign, cart);
        if (!Array.isArray(campaign.segmentsData?.segmentsList) || !Array.isArray(results) ||
            results.length !== campaign.segmentsData.segmentsList.length || results.some((result) => typeof result?.met !== 'boolean')) {
          throw new Error('Unsupported Kite eligibility results');
        }
        const campaignGifts = engine.cart._buildDesiredState(campaign, campaignIndex, results);
        if (!Array.isArray(campaignGifts) || campaignGifts.some((gift) =>
          !Number.isFinite(Number(gift.variantId)) || Number(gift.variantId) <= 0 ||
          !Number.isFinite(Number(gift.quantity)) || Number(gift.quantity) <= 0 || !gift.ruleId
        )) throw new Error('Unsupported Kite gift results');
        campaigns.push({ campaign, results, gifts: campaignGifts });
        gifts.push(...campaignGifts);
      }
      return { campaigns, gifts };
    } catch {
      this.reportCompatibilityError();
      return null;
    }
  }

  isQualifyingItem(cart, item, offer) {
    const engine = this.getEngine();
    if (!engine || !offer || !item) return false;
    const cartWithoutItem = { ...cart, items: cart.items.filter((cartItem) => cartItem.key !== item.key) };
    try {
      return offer.campaigns.some(({ campaign, results, gifts }) => {
        if (!gifts.length) return false;
        const productRuleTypes = ['products', 'productVariants', 'collections'];
        const globalProductRules = campaign.discountTriggeredRulesData?.rulesGlobalList?.[0]?.rulesList?.filter((rule) =>
          productRuleTypes.includes(rule.ruleType)
        ) || [];
        if (globalProductRules.length) {
          const globalSegment = { buyXRulesData: { rulesList: globalProductRules } };
          const globalConditions = engine.rules.evaluate(globalSegment, cart, campaign).buyXResults;
          const previousGlobalConditions = engine.rules.evaluate(globalSegment, cartWithoutItem, campaign).buyXResults;
          if (!Array.isArray(globalConditions) || !Array.isArray(previousGlobalConditions) ||
              !globalConditions.some((condition, index) => Number(condition.currentValue) > Number(previousGlobalConditions[index]?.currentValue || 0))) return false;
        }
        const previousResults = engine.rules.evaluateAll(campaign, cartWithoutItem);
        if (!Array.isArray(previousResults)) throw new Error('Unsupported Kite eligibility results');
        return results.some((result, index) => {
          const segmentId = campaign.segmentsData?.segmentsList?.[index]?.segmentId || `fg_rule_${index}`;
          if (!result.met || !gifts.some((gift) => String(gift.ruleId) === String(segmentId))) return false;
          const conditions = result.buyXResults;
          const previousConditions = previousResults[index]?.buyXResults;
          if (!Array.isArray(conditions) || !Array.isArray(previousConditions)) return false;
          const hasProductConditions = conditions.some((condition) =>
            productRuleTypes.includes(condition.ruleType)
          );
          return conditions.some((condition, conditionIndex) => {
            if (hasProductConditions && !productRuleTypes.includes(condition.ruleType)) return false;
            return Number(condition.currentValue) > Number(previousConditions[conditionIndex]?.currentValue || 0);
          });
        });
      });
    } catch {
      this.reportCompatibilityError();
      return false;
    }
  }
}

class KiteGiftGuard {
  #cartAttributes = null;
  #wrappedKiteMethods = new WeakSet();
  #hasPendingCartReconciliation = false;
  #giftAdditionQueue = Promise.resolve();
  #wrappedFetchMethods = new WeakSet();
  #pendingCartResource = null;
  #cartMutationVersion = 0;
  #activeCartMutationCount = 0;
  #lastLegacyCartSignature = null;
  #isReconciliationScheduled = false;

  constructor() {
    try {
      this.#cartAttributes = JSON.parse(document.querySelector('[data-gift-countdown-attributes]')?.dataset.giftCountdownAttributes || 'null');
    } catch {}
    this.installRequestGuard();
    if (isCountdownEnabled) {
      document.addEventListener(ThemeEvents.cartUpdate, (event) => {
        if (event.detail?.data?.source !== 'kite') return;
        const cart = event.detail.resource;
        if (!Array.isArray(cart?.items) || !cart.attributes) return;
        event.stopImmediatePropagation();
        const signature = JSON.stringify([cart.items, cart.attributes]);
        if (signature === this.#lastLegacyCartSignature) return;
        this.#lastLegacyCartSignature = signature;
        this.scheduleCartReconciliation(true, cart);
      }, true);
    }
  }

  installRequestGuard() {
    if (!isCountdownEnabled || this.#wrappedFetchMethods.has(window.fetch)) return;
    const originalFetch = window.fetch;
    const giftGuard = this;
    window.fetch = async function (input, options) {
      const requestUrl = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
      const isCartMutation = requestUrl.origin === location.origin && /\/cart\/(add|change|update|clear)(?:\.js)?$/.test(requestUrl.pathname);
      if (!isCartMutation) return originalFetch.call(this, input, options);
      const isGiftAddition = /\/cart\/add(?:\.js)?$/.test(requestUrl.pathname);
      let hasGiftItems = false;
      if (isGiftAddition) {
        let body = options?.body;
        if (body === undefined && input instanceof Request) body = await input.clone().text();
        let payload = null;
        if (typeof body === 'string') {
          try { payload = JSON.parse(body); } catch {
            const formFields = new URLSearchParams(body);
            payload = { properties: { _free_product: formFields.get('properties[_free_product]') } };
          }
        } else if (body instanceof FormData || body instanceof URLSearchParams) {
          payload = { properties: { _free_product: body.get('properties[_free_product]') } };
        }
        const items = Array.isArray(payload?.items) ? payload.items : payload && !payload.items ? [payload] : [];
        hasGiftItems = items.some((item) => String(item?.properties?._free_product) === 'true');
        if (hasGiftItems && giftGuard.shouldBlockGiftAddition()) {
          const paidItems = items.filter((item) => String(item?.properties?._free_product) !== 'true');
          if (!paidItems.length) return new Response(JSON.stringify({ items: [] }), {
            status: 200, headers: { 'Content-Type': 'application/json' },
          });
          const headers = new Headers(options?.headers || (input instanceof Request ? input.headers : undefined));
          headers.set('Content-Type', 'application/json');
          options = { ...options, headers, body: JSON.stringify({ ...payload, items: paidItems }) };
        }
      }
      const requestVersion = ++giftGuard.#cartMutationVersion;
      giftGuard.#activeCartMutationCount++;
      let response;
      try {
        response = await originalFetch.call(this, input, options);
      } finally {
        giftGuard.#activeCartMutationCount--;
        giftGuard.#cartMutationVersion++;
      }
      if (!giftGuard.#activeCartMutationCount && giftGuard.#cartMutationVersion === requestVersion + 1) {
        cartResourceVersions.set(response, giftGuard.#cartMutationVersion);
      }
      if (response.ok) {
        invalidateCartRead();
        if (hasGiftItems || requestUrl.searchParams.get('app') === 'gfgfreegift') {
          giftGuard.scheduleCartReconciliation(true);
        }
      }
      return response;
    };
    this.#wrappedFetchMethods.add(window.fetch);
  }

  addGiftItems(cartAddition, addToCart) {
    const requestedItems = cartAddition?.items || [cartAddition];
    if (!requestedItems.some((item) => String(item?.properties?._free_product) === 'true')) {
      return addToCart(cartAddition);
    }
    const queuedAddition = this.#giftAdditionQueue.then(async () => {
      if (this.shouldBlockGiftAddition()) {
        const paidItems = requestedItems.filter((item) => String(item?.properties?._free_product) !== 'true');
        return paidItems.length ? addToCart({ ...cartAddition, items: paidItems }) : { items: [] };
      }
      invalidateCartRead();
      const currentCart = await readCart();
      const existingGiftQuantities = new Map();
      currentCart.items.forEach((item) => {
        if (String(item.properties?._free_product) !== 'true') return;
        const variantId = Number(item.variant_id);
        existingGiftQuantities.set(variantId, (existingGiftQuantities.get(variantId) || 0) + item.quantity);
      });
      const allowedItems = [];
      for (const item of requestedItems) {
        if (String(item?.properties?._free_product) !== 'true') {
          allowedItems.push(item);
          continue;
        }
        if (this.shouldBlockGiftAddition(currentCart)) continue;
        const variantId = Number(item.id);
        const requestedQuantity = Number(item.quantity) || 1;
        const existingQuantity = existingGiftQuantities.get(variantId) || 0;
        if (existingQuantity >= requestedQuantity) continue;
        allowedItems.push({ ...item, quantity: requestedQuantity - existingQuantity });
        existingGiftQuantities.set(variantId, requestedQuantity);
      }
      if (!allowedItems.length) return { items: [] };
      const additionResult = await addToCart({ ...cartAddition, items: allowedItems });
      this.scheduleCartReconciliation();
      return additionResult;
    });
    this.#giftAdditionQueue = queuedAddition.catch(() => {});
    return queuedAddition;
  }

  getDuplicateGiftUpdates(cart, offer) {
    if (!offer) return {};
    const desiredGiftQuantities = new Map();
    const desiredGiftRuleIds = new Map();
    for (const gift of offer.gifts) {
      const variantId = Number(gift.variantId);
      desiredGiftQuantities.set(variantId, Math.max(desiredGiftQuantities.get(variantId) || 0, Number(gift.quantity)));
      const activeRuleIds = desiredGiftRuleIds.get(variantId) || new Set();
      activeRuleIds.add(String(gift.ruleId));
      desiredGiftRuleIds.set(variantId, activeRuleIds);
    }
    const giftsByVariant = new Map();
    for (const item of cart.items) {
      if (String(item.properties?._free_product) !== 'true') continue;
      const variantId = Number(item.variant_id);
      const giftLines = giftsByVariant.get(variantId) || [];
      giftLines.push(item);
      giftsByVariant.set(variantId, giftLines);
    }
    const quantityUpdates = {};
    const knownRuleIds = new Set((kiteGiftRules.getEngine()?.state.campaigns || []).flatMap((campaign) =>
      (campaign.segmentsData?.segmentsList || []).map((segment, index) => String(segment.segmentId || `fg_rule_${index}`))
    ));
    for (const [variantId, giftLines] of giftsByVariant) {
      const desiredQuantity = desiredGiftQuantities.get(variantId);
      if (!desiredQuantity) {
        giftLines.forEach((item) => {
          if (knownRuleIds.has(String(item.properties?._rule_id))) quantityUpdates[item.key] = 0;
        });
        continue;
      }
      const currentQuantity = giftLines.reduce((total, item) => total + item.quantity, 0);
      const retainedQuantity = Math.min(currentQuantity, desiredQuantity);
      const activeRuleIds = desiredGiftRuleIds.get(variantId);
      giftLines.sort((first, second) =>
        Number(activeRuleIds.has(String(second.properties?._rule_id))) -
        Number(activeRuleIds.has(String(first.properties?._rule_id)))
      );
      const [retainedGift, ...duplicateGifts] = giftLines;
      if (retainedGift.quantity !== retainedQuantity) quantityUpdates[retainedGift.key] = retainedQuantity;
      duplicateGifts.forEach((item) => { quantityUpdates[item.key] = 0; });
    }
    return quantityUpdates;
  }

  scheduleCartReconciliation(deferReconciliation = false, cart = null) {
    this.#pendingCartResource = cart && !giftCountdownManager.isSynchronizing() ? cart : null;
    this.#hasPendingCartReconciliation = true;
    if (!deferReconciliation) {
      this.reconcileCompletedCartUpdates();
    } else if (!this.#isReconciliationScheduled) {
      this.#isReconciliationScheduled = true;
      queueMicrotask(() => {
        this.#isReconciliationScheduled = false;
        this.reconcileCompletedCartUpdates();
      });
    }
  }

  reconcileCompletedCartUpdates() {
    if (!this.#hasPendingCartReconciliation || window.gfg?.gfgFreeGiftV2?.state?.isProcessing ||
        giftCountdownManager.isSynchronizing()) return;
    this.#hasPendingCartReconciliation = false;
    const pendingCart = this.#pendingCartResource;
    this.#pendingCartResource = null;
    invalidateCartRead();
    giftCountdownManager.synchronizeOffer({ isKiteNotification: true, cart: pendingCart });
  }

  updateCartAttributes(cart) {
    this.#cartAttributes = cart.attributes || {};
    this.installGiftGuards();
  }

  getCartVersion() {
    return this.#activeCartMutationCount ? null : this.#cartMutationVersion;
  }

  canReuseCart(cart) {
    const version = this.getCartVersion();
    return version !== null && cartResourceVersions.get(cart) === version;
  }

  shouldBlockGiftAddition(cart) {
    if (!isCountdownEnabled) return false;
    const attributes = this.#cartAttributes || cart?.attributes || {};
    return attributes[CART_ATTRIBUTE_KEYS.state] === 'expired' ||
      (attributes[CART_ATTRIBUTE_KEYS.state] === 'active' && Number(attributes[CART_ATTRIBUTE_KEYS.deadline]) <= Date.now());
  }

  installGiftGuards() {
    if (!isCountdownEnabled) return;
    this.installRequestGuard();
    const giftGuard = this;
    const kiteUtility = window.gfg?.utility;
    const kiteEngine = kiteGiftRules.getEngine();
    const kiteCart = kiteEngine?.cart;
    this.keepKiteOutOfTheDrawer();
    if (kiteEngine && !this.#wrappedKiteMethods.has(kiteEngine._processCartChange)) {
      const originalProcessCartChange = kiteEngine._processCartChange;
      kiteEngine._processCartChange = async function (...args) {
        if (giftCountdownManager.isProcessingSynchronization()) {
          kiteCartSynchronizer.scheduleEligibilityRefresh();
          return;
        }
        const wasProcessing = this.state.isProcessing;
        try {
          return await originalProcessCartChange.apply(this, args);
        } finally {
          if (!wasProcessing && !this.state.isProcessing) {
            queueMicrotask(() => {
              giftCountdownManager.resumeSynchronization();
              giftGuard.reconcileCompletedCartUpdates();
              kiteCartSynchronizer.refreshGiftEligibility();
            });
          }
        }
      };
      this.#wrappedKiteMethods.add(kiteEngine._processCartChange);
    }
    if (kiteUtility && typeof kiteUtility.addToCartV2 === 'function' && !this.#wrappedKiteMethods.has(kiteUtility.addToCartV2)) {
      const originalAddToCart = kiteUtility.addToCartV2;
      kiteUtility.addToCartV2 = function (cartAddition, ...additionalArguments) {
        return giftGuard.addGiftItems(cartAddition, (allowedAddition) =>
          originalAddToCart.call(this, allowedAddition, ...additionalArguments)
        );
      };
      this.#wrappedKiteMethods.add(kiteUtility.addToCartV2);
    }
    if (kiteCart && typeof kiteCart.syncGiftsWithCart === 'function' && !this.#wrappedKiteMethods.has(kiteCart.syncGiftsWithCart)) {
      const originalSyncGifts = kiteCart.syncGiftsWithCart;
      kiteCart.syncGiftsWithCart = async function (campaign, campaignIndex, eligibilityResults, cartResource) {
        if (giftGuard.shouldBlockGiftAddition(cartResource)) return { changed: false, giftsAdded: false };
        const synchronizationResult = await originalSyncGifts.call(this, campaign, campaignIndex, eligibilityResults, cartResource);
        if (synchronizationResult?.changed) giftGuard.scheduleCartReconciliation();
        return synchronizationResult;
      };
      this.#wrappedKiteMethods.add(kiteCart.syncGiftsWithCart);
    }
  }

  keepKiteOutOfTheDrawer() {
    const kite = window.gfg;
    if (!kite) return;
    if (typeof kite.cartDrawerObserver?.reinjectWidgets === 'function' && !this.#wrappedKiteMethods.has(kite.cartDrawerObserver.reinjectWidgets)) {
      kite.cartDrawerObserver.reinjectWidgets = () => {};
      this.#wrappedKiteMethods.add(kite.cartDrawerObserver.reinjectWidgets);
    }
    if (typeof kite.gfgCartRefreshHelper?.refreshCartForTheme === 'function' && !this.#wrappedKiteMethods.has(kite.gfgCartRefreshHelper.refreshCartForTheme)) {
      kite.gfgCartRefreshHelper.refreshCartForTheme = () => true;
      this.#wrappedKiteMethods.add(kite.gfgCartRefreshHelper.refreshCartForTheme);
    }
    if (kite.utility && !kite.utility.skipGraphqlChecks && typeof kite.utility.callchecksAfterApiCalls === 'function') {
      const originalChecks = kite.utility.callchecksAfterApiCalls;
      kite.utility.skipGraphqlChecks = true;
      kite.utility.callchecksAfterApiCalls = function (url, data) {
        if (typeof url === 'string' && url.includes('graphql.json')) return data;
        return originalChecks.apply(this, arguments);
      };
    }
  }
}

class CartGiftCountdownManager {
  #currentCart = null;
  #isSynchronizing = false;
  #isSynchronizationScheduled = false;
  #pendingSynchronizations = [];
  #lastExpirationAttemptAt = 0;
  #expirationAttemptCount = 0;
  #isExpirationPending = false;
  #isCheckoutPending = false;
  #checkoutNavigationVersion = 0;
  #checkoutAbortController = null;
  #checkoutPause = null;
  #qualifyingAdditionAt = 0;
  #pendingQualifyingAdditions = [];
  #scheduledKiteSyncAt = 0;
  #giftRemovalAttemptCount = 0;
  #cartBroadcastChannel = isCountdownEnabled && typeof BroadcastChannel === 'function' ? new BroadcastChannel('organics-gift-countdown') : null;

  constructor() {
    try {
      this.#checkoutPause = JSON.parse(sessionStorage.getItem(CHECKOUT_PAUSE_STORAGE_KEY) || 'null');
    } catch {}
    this.synchronizeOffer({ shouldResumeCountdown: Boolean(this.#checkoutPause) });
    if (!isCountdownEnabled) return;
    if (this.#cartBroadcastChannel) this.#cartBroadcastChannel.onmessage = () => this.synchronizeOffer();
    document.addEventListener(ThemeEvents.cartUpdate, (event) => {
      const eventData = event.detail?.data || {};
      if (eventData.source === COUNTDOWN_EVENT_SOURCE || eventData.didError) return;
      const eventCart = event.detail?.resource;
      const hasCompleteCartResource = Array.isArray(eventCart?.items) && Boolean(eventCart.attributes);
      const isProductAddition = ['product-form-component', 'cart-bundle-component'].includes(eventData.source);
      if (isProductAddition || eventData.source === 'cart-items-component') {
        this.#giftRemovalAttemptCount = 0;
        this.#expirationAttemptCount = 0;
      }
      this.synchronizeOffer({ isProductAddition, cart: hasCompleteCartResource && !this.#isSynchronizing && !this.#pendingSynchronizations.length ? eventCart : null,
        changedSubscriptionLineKey: eventData.source === 'cart-items-component' ? eventData.changedLineKey : null,
        quantityDelta: eventData.quantityDelta,
        variantId: Number(eventData.variantId || event.detail?.sourceId),
        sellingPlanId: Number(eventData.sellingPlanId), addedAt: Date.now() });
      if (isProductAddition || eventData.source === 'cart-items-component') {
        this.#scheduleKiteReconciliation();
      }
    });
    document.addEventListener(ThemeEvents.discountUpdate, () => this.synchronizeOffer());
    window.addEventListener('pagehide', () => {
      this.#checkoutNavigationVersion++;
      this.#checkoutAbortController?.abort();
      this.#isCheckoutPending = false;
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.#resumeStoredCountdown();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) return;
      if (!this.#isCheckoutPending && this.#checkoutPause) {
        this.#resumeStoredCountdown();
      } else {
        this.synchronizeOffer();
      }
    });
    document.addEventListener('submit', this.#handleCheckoutSubmit, true);
    document.addEventListener('click', this.#handleCheckoutClick, true);
    setInterval(() => this.#handleCountdownTick(), 1000);
  }

  #scheduleKiteReconciliation() {
    if (isCountdownEnabled) this.#scheduledKiteSyncAt = Date.now() + 3000;
  }

  #resumeStoredCountdown() {
    invalidateCartRead();
    this.#pendingSynchronizations.forEach(({ context }) => { context.cart = null; });
    return this.synchronizeOffer({ shouldResumeCountdown: Boolean(this.#checkoutPause) });
  }

  #storeCheckoutPause(cart) {
    const attributes = cart?.attributes || {};
    const state = attributes[CART_ATTRIBUTE_KEYS.state];
    if (!cart?.token || !['active', 'paused'].includes(state)) return;
    const deadline = Number(attributes[CART_ATTRIBUTE_KEYS.deadline]);
    const remaining = state === 'paused' ? Number(attributes[CART_ATTRIBUTE_KEYS.remaining]) : deadline - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) return;
    this.#checkoutPause = { token: cart.token, deadline, remaining, paidItems: getPaidItemsSignature(cart) };
    try {
      sessionStorage.setItem(CHECKOUT_PAUSE_STORAGE_KEY, JSON.stringify(this.#checkoutPause));
    } catch {}
  }

  #clearCheckoutPause() {
    this.#checkoutPause = null;
    try {
      sessionStorage.removeItem(CHECKOUT_PAUSE_STORAGE_KEY);
    } catch {}
  }

  hasPendingQualifyingAddition() {
    return this.#pendingQualifyingAdditions.length > 0;
  }

  isSynchronizing() {
    return this.#isCheckoutPending || this.#isSynchronizing || this.#pendingSynchronizations.length > 0;
  }

  isProcessingSynchronization() {
    return this.#isSynchronizing || this.#isCheckoutPending;
  }

  resumeSynchronization() {
    if (this.#pendingSynchronizations.length) this.#processSynchronizationQueue();
  }

  #getKiteGiftItems(cart) {
    return cart.items.filter((item) => String(item.properties?._free_product) === 'true' &&
      Boolean(item.properties?._rule_id || item.properties?._kite_promo_name));
  }

  #getPaidSubscriptionItems(cart) {
    return cart.items.filter((item) => item.final_line_price > 0 &&
      Boolean(item.selling_plan_allocation?.selling_plan?.id) &&
      String(item.properties?._free_product) !== 'true');
  }

  #getCartItemsSignature(cart) {
    return JSON.stringify((cart?.items || []).map((item) =>
      [item.key, item.quantity, item.final_line_price, item.selling_plan_allocation?.selling_plan?.id]
    ));
  }

  async #fetchCurrentCart() {
    const version = kiteGiftGuard.getCartVersion();
    const cart = await readCart();
    if (version !== null && version === kiteGiftGuard.getCartVersion()) cartResourceVersions.set(cart, version);
    kiteGiftGuard.updateCartAttributes(cart);
    return cart;
  }

  async #updateCartOffer(cart, attributes, removeGifts = false, offer = null) {
    let giftQuantityUpdates = removeGifts ? {} : kiteGiftGuard.getDuplicateGiftUpdates(cart, offer);
    if (removeGifts) this.#getKiteGiftItems(cart).forEach((item) => { giftQuantityUpdates[item.key] = 0; });
    if (removeGifts && Object.keys(giftQuantityUpdates).length) {
      if (this.#giftRemovalAttemptCount >= 3) {
        if (this.#giftRemovalAttemptCount === 3) {
          console.warn('[cart-gift-countdown] Gift removal stopped after three attempts.');
          this.#giftRemovalAttemptCount++;
        }
        giftQuantityUpdates = {};
      } else {
        this.#giftRemovalAttemptCount++;
      }
    }
    const changedAttributes = Object.fromEntries(
      Object.entries(attributes).filter(([key, value]) => String(cart.attributes?.[key] ?? '') !== value)
    );
    if (!Object.keys(changedAttributes).length && !Object.keys(giftQuantityUpdates).length) return cart;

    const sectionIds = Object.keys(giftQuantityUpdates).length ? [...new Set(
      [...document.querySelectorAll('cart-items-component')].map((countdownElement) => countdownElement.dataset.sectionId).filter(Boolean)
    )].slice(0, 5) : [];
    invalidateCartRead();
    const writeCartChanges = (cartAttributes, quantityUpdates) => requestCartResource(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: cartAttributes,
        updates: quantityUpdates,
        ...(sectionIds.length ? { sections: sectionIds, sections_url: window.location.pathname } : {}),
      }),
    });
    let updatedCart;
    try {
      updatedCart = await writeCartChanges(changedAttributes, giftQuantityUpdates);
    } catch (error) {
      if (error.status !== 422 || !Object.keys(giftQuantityUpdates).length) throw error;
      invalidateCartRead();
      const latestCart = await this.#fetchCurrentCart();
      const remainingGiftUpdates = removeGifts
        ? Object.fromEntries(this.#getKiteGiftItems(latestCart).map((item) => [item.key, 0]))
        : kiteGiftGuard.getDuplicateGiftUpdates(latestCart, kiteGiftRules.getOffer(latestCart));
      const remainingAttributeUpdates = Object.fromEntries(Object.entries(attributes).filter(([key, value]) =>
        String(latestCart.attributes?.[key] ?? '') !== value
      ));
      updatedCart = Object.keys(remainingGiftUpdates).length || Object.keys(remainingAttributeUpdates).length
        ? await writeCartChanges(remainingAttributeUpdates, remainingGiftUpdates)
        : latestCart;
    }
    invalidateCartRead();
    this.#currentCart = updatedCart;
    kiteGiftGuard.updateCartAttributes(updatedCart);
    if (sectionIds.length || this.#getCartItemsSignature(updatedCart) !== this.#getCartItemsSignature(cart)) {
      document.dispatchEvent(new CartUpdateEvent(updatedCart, COUNTDOWN_EVENT_SOURCE, {
        source: COUNTDOWN_EVENT_SOURCE,
        itemCount: updatedCart.item_count,
        sections: updatedCart.sections,
      }));
    }
    this.#cartBroadcastChannel?.postMessage('cart-update');
    return updatedCart;
  }

  async #reconcileGiftOffer(context) {
    let cart = context.cart || await this.#fetchCurrentCart();
    if (isCountdownEnabled && window.gfg?.gfgFreeGiftV2?.state?.isProcessing && !context.shouldExpireCountdown) return false;
    if (context.cart) kiteGiftGuard.updateCartAttributes(cart);
    const itemsChanged = this.#getCartItemsSignature(cart) !== this.#getCartItemsSignature(this.#currentCart);
    const previousCartAttributes = cart.attributes || {};
    const paidSubscriptionItems = this.#getPaidSubscriptionItems(cart);
    const subscriptionItemCount = paidSubscriptionItems.reduce((total, item) => total + item.quantity, 0);
    const giftItems = this.#getKiteGiftItems(cart);
    let state = previousCartAttributes[CART_ATTRIBUTE_KEYS.state] || 'inactive';
    let deadlineAt = Number(previousCartAttributes[CART_ATTRIBUTE_KEYS.deadline]) || 0;
    let remainingMilliseconds = Number(previousCartAttributes[CART_ATTRIBUTE_KEYS.remaining]) || 0;

    if (!isCountdownEnabled) {
      this.#clearCheckoutPause();
      if (previousCartAttributes[CART_ATTRIBUTE_KEYS.enabled] === 'true') {
        cart = await this.#updateCartOffer(cart, {
          [CART_ATTRIBUTE_KEYS.enabled]: 'false', [CART_ATTRIBUTE_KEYS.deadline]: '', [CART_ATTRIBUTE_KEYS.state]: 'inactive',
          [CART_ATTRIBUTE_KEYS.count]: '', [CART_ATTRIBUTE_KEYS.items]: '', [CART_ATTRIBUTE_KEYS.available]: 'true', [CART_ATTRIBUTE_KEYS.remaining]: '',
        });
      }
      this.#currentCart = cart;
      this.renderCountdown();
      return;
    }

    const checkoutPause = context.shouldResumeCountdown ? this.#checkoutPause : null;
    if (checkoutPause && checkoutPause.token === cart.token && checkoutPause.deadline === deadlineAt &&
        checkoutPause.paidItems === getPaidItemsSignature(cart) &&
        Number.isFinite(checkoutPause.remaining) && checkoutPause.remaining > 0 &&
        (state === 'active' || state === 'paused')) {
      remainingMilliseconds = checkoutPause.remaining;
      state = 'paused';
    }

    const addedSubscriptionItem = context.isProductAddition && context.sellingPlanId > 0 && paidSubscriptionItems.find((item) =>
      item.variant_id === context.variantId &&
      Number(item.selling_plan_allocation.selling_plan.id) === context.sellingPlanId
    );
    const changedSubscriptionItem = context.changedSubscriptionLineKey && paidSubscriptionItems.find((item) =>
      item.key === context.changedSubscriptionLineKey
    );
    const hasSubscriptionQuantityIncreased = Boolean(changedSubscriptionItem && context.quantityDelta > 0);
    if (changedSubscriptionItem && context.quantityDelta < 0) {
      this.#pendingQualifyingAdditions = this.#pendingQualifyingAdditions.filter((addition) =>
        addition.variantId !== changedSubscriptionItem.variant_id ||
        addition.sellingPlanId !== Number(changedSubscriptionItem.selling_plan_allocation.selling_plan.id)
      );
    }
    if (state === 'active' && deadlineAt <= Date.now()) state = 'expired';
    const candidateItem = addedSubscriptionItem || (hasSubscriptionQuantityIncreased ? changedSubscriptionItem : null);
    const additionAt = context.addedAt || Date.now();
    if (candidateItem && state !== 'active' && state !== 'paused' && (state !== 'expired' || additionAt >= deadlineAt)) {
      const pendingAddition = {
        variantId: candidateItem.variant_id,
        sellingPlanId: Number(candidateItem.selling_plan_allocation.selling_plan.id),
        addedAt: additionAt,
      };
      if (!this.#pendingQualifyingAdditions.some((addition) => addition.variantId === pendingAddition.variantId &&
          addition.sellingPlanId === pendingAddition.sellingPlanId)) this.#pendingQualifyingAdditions.push(pendingAddition);
    }
    const hasCurrentEligibility = kiteCartSynchronizer.hasCurrentEligibility(cart);
    const offer = hasCurrentEligibility || context.isKiteNotification ? kiteGiftRules.getOffer(cart) : null;
    let hasQualifyingAddition = false;
    this.#pendingQualifyingAdditions = this.#pendingQualifyingAdditions.filter((pendingAddition) => {
      const pendingItem = paidSubscriptionItems.find((item) => item.variant_id === pendingAddition.variantId &&
        Number(item.selling_plan_allocation.selling_plan.id) === pendingAddition.sellingPlanId);
      if (!pendingItem || Date.now() - pendingAddition.addedAt >= COUNTDOWN_DURATION_MS) return false;
      if (offer && hasCurrentEligibility) {
        if (kiteGiftRules.isQualifyingItem(cart, pendingItem, offer)) {
          hasQualifyingAddition = true;
          this.#qualifyingAdditionAt = this.#qualifyingAdditionAt ? Math.min(this.#qualifyingAdditionAt, pendingAddition.addedAt) : pendingAddition.addedAt;
        }
        return false;
      }
      return true;
    });
    if (this.#pendingQualifyingAdditions.length) kiteCartSynchronizer.scheduleEligibilityRefresh();
    if (!subscriptionItemCount || Boolean(offer && !offer.gifts.length)) {
      state = 'inactive';
      deadlineAt = 0;
      remainingMilliseconds = 0;
      this.#qualifyingAdditionAt = 0;
      this.#pendingQualifyingAdditions = [];
    } else if (state === 'paused' && context.shouldResumeCountdown) {
      deadlineAt = Date.now() + remainingMilliseconds;
      state = remainingMilliseconds > 0 ? 'active' : 'expired';
    } else if (state !== 'paused') {
      if ((state === 'expired' && hasQualifyingAddition) ||
          (state === 'inactive' && giftItems.length && offer?.gifts.length)) {
        deadlineAt = (this.#qualifyingAdditionAt || Date.now()) + COUNTDOWN_DURATION_MS;
        state = 'active';
        this.#giftRemovalAttemptCount = 0;
        this.#expirationAttemptCount = 0;
        this.#qualifyingAdditionAt = 0;
      }
    }
    if (state === 'active') {
      this.#qualifyingAdditionAt = 0;
      this.#pendingQualifyingAdditions = [];
    }

    if (state === 'inactive' && subscriptionItemCount > 0 && !giftItems.length && !offer) {
      if (context.shouldResumeCountdown) this.#clearCheckoutPause();
      this.#currentCart = cart;
      this.renderCountdown();
      return;
    }

    const shouldRemoveGifts = state === 'expired' || !subscriptionItemCount || Boolean(offer && !offer.gifts.length);
    const hasGiftCorrections = Object.keys(kiteGiftGuard.getDuplicateGiftUpdates(cart, offer)).length > 0;
    const shouldDisplayGiftsImmediately = context.isKiteNotification && itemsChanged && !shouldRemoveGifts && !hasGiftCorrections;
    if (shouldDisplayGiftsImmediately) {
      this.#currentCart = cart;
      document.dispatchEvent(new CartUpdateEvent(cart, COUNTDOWN_EVENT_SOURCE, {
        source: COUNTDOWN_EVENT_SOURCE, itemCount: cart.item_count, sections: cart.sections,
      }));
    }
    cart = await this.#updateCartOffer(cart, {
      [CART_ATTRIBUTE_KEYS.enabled]: 'true',
      [CART_ATTRIBUTE_KEYS.deadline]: deadlineAt ? String(deadlineAt) : '',
      [CART_ATTRIBUTE_KEYS.state]: state,
      [CART_ATTRIBUTE_KEYS.count]: '',
      [CART_ATTRIBUTE_KEYS.items]: '',
      [CART_ATTRIBUTE_KEYS.available]: String(!shouldRemoveGifts && (state === 'active' || state === 'paused')),
      [CART_ATTRIBUTE_KEYS.remaining]: state === 'paused' ? String(remainingMilliseconds) : '',
    }, shouldRemoveGifts, offer);
    if (context.shouldResumeCountdown) this.#clearCheckoutPause();
    const currentCartLineKeys = new Set(cart.items.map((item) => item.key));
    const hasRemovedCartRows = [...document.querySelectorAll('cart-items-component tr[data-key]')]
      .some((row) => !currentCartLineKeys.has(row.dataset.key));
    if ((hasRemovedCartRows || (context.isKiteNotification && (itemsChanged || (context.shouldExpireCountdown && state === 'expired')))) &&
        !cart.sections && !shouldDisplayGiftsImmediately) {
      this.#currentCart = cart;
      document.dispatchEvent(new CartUpdateEvent(cart, COUNTDOWN_EVENT_SOURCE, { source: COUNTDOWN_EVENT_SOURCE, itemCount: cart.item_count }));
    }
    this.#currentCart = cart;
    this.renderCountdown();
    const hasAllExpectedGifts = offer && offer.gifts.every((gift) => cart.items
      .filter((item) => String(item.properties?._free_product) === 'true' && Number(item.variant_id) === Number(gift.variantId))
      .reduce((quantity, item) => quantity + item.quantity, 0) >= Number(gift.quantity));
    const hasCorrectGiftQuantities = offer && !Object.keys(kiteGiftGuard.getDuplicateGiftUpdates(cart, offer)).length;
    if (context.isKiteNotification && !window.gfg?.gfgFreeGiftV2?.state?.isProcessing &&
        ((state === 'expired' && !this.#getKiteGiftItems(cart).length) ||
          (kiteCartSynchronizer.hasCurrentEligibility(cart) && hasAllExpectedGifts && hasCorrectGiftQuantities))) {
      this.#scheduledKiteSyncAt = 0;
    }
    if (previousCartAttributes[CART_ATTRIBUTE_KEYS.state] === 'expired' && state === 'active') {
      kiteCartSynchronizer.scheduleEligibilityRefresh(true);
    }
  }

  synchronizeOffer(context = {}) {
    return new Promise((resolve, reject) => {
      const pendingSynchronization = !context.shouldResumeCountdown && !context.shouldExpireCountdown && !context.isProductAddition && !context.changedSubscriptionLineKey && this.#pendingSynchronizations.find((queuedSynchronization) =>
        !queuedSynchronization.context.shouldExpireCountdown && !queuedSynchronization.context.isProductAddition && !queuedSynchronization.context.changedSubscriptionLineKey
      );
      if (pendingSynchronization) {
        pendingSynchronization.context.isKiteNotification ||= context.isKiteNotification;
        pendingSynchronization.context.shouldResumeCountdown ||= context.shouldResumeCountdown;
        pendingSynchronization.context.cart = null;
        pendingSynchronization.completionCallbacks.push({ resolve, reject });
      } else {
        const synchronization = { context, completionCallbacks: [{ resolve, reject }] };
        if (context.shouldResumeCountdown || context.shouldExpireCountdown) this.#pendingSynchronizations.unshift(synchronization);
        else this.#pendingSynchronizations.push(synchronization);
      }
      if (!this.#isSynchronizing && !this.#isSynchronizationScheduled) {
        this.#isSynchronizationScheduled = true;
        queueMicrotask(() => {
          this.#isSynchronizationScheduled = false;
          this.#processSynchronizationQueue();
        });
      }
    }).catch(() => {});
  }

  async #processSynchronizationQueue() {
    if (this.#isSynchronizing || this.#isCheckoutPending) return;
    if (isCountdownEnabled && window.gfg?.gfgFreeGiftV2?.state?.isProcessing &&
        !this.#pendingSynchronizations[0]?.context.shouldExpireCountdown) {
      this.#pendingSynchronizations.forEach(({ context }) => { context.cart = null; });
      return;
    }
    this.#isSynchronizing = true;
    while (this.#pendingSynchronizations.length && !this.#isCheckoutPending) {
      const { context, completionCallbacks } = this.#pendingSynchronizations.shift();
      try {
        const completed = navigator.locks
          ? await navigator.locks.request('organics-free-gift-cart', () => this.#reconcileGiftOffer(context))
          : await this.#reconcileGiftOffer(context);
        if (completed === false) {
          this.#pendingSynchronizations.unshift({ context: { ...context, cart: null }, completionCallbacks });
          break;
        }
        completionCallbacks.forEach(({ resolve }) => resolve());
      } catch (error) {
        console.error('[cart-gift-countdown]', error);
        completionCallbacks.forEach(({ reject }) => reject(error));
      }
    }
    this.#isSynchronizing = false;
    kiteGiftGuard.reconcileCompletedCartUpdates();
    kiteCartSynchronizer.refreshGiftEligibility(this.#currentCart);
  }

  #handleCountdownTick() {
    if (this.#isCheckoutPending) return;
    kiteGiftGuard.installGiftGuards();
    const cartAttributes = this.#currentCart?.attributes || {};
    const hasExpired = cartAttributes[CART_ATTRIBUTE_KEYS.state] === 'expired' ||
      (cartAttributes[CART_ATTRIBUTE_KEYS.state] === 'active' && Number(cartAttributes[CART_ATTRIBUTE_KEYS.deadline]) <= Date.now());
    const needsExpirationUpdate = cartAttributes[CART_ATTRIBUTE_KEYS.state] !== 'expired' ||
      (this.#currentCart && this.#getKiteGiftItems(this.#currentCart).length > 0);
    if (isCountdownEnabled && !this.#checkoutPause && hasExpired && needsExpirationUpdate && !this.#isExpirationPending &&
        this.#expirationAttemptCount < 3 && Date.now() - this.#lastExpirationAttemptAt > 5000) {
      this.#isExpirationPending = true;
      this.#expirationAttemptCount++;
      this.#lastExpirationAttemptAt = Date.now();
      this.synchronizeOffer({ shouldExpireCountdown: true, isKiteNotification: true }).finally(() => {
        this.#isExpirationPending = false;
      });
    }
    this.resumeSynchronization();
    kiteGiftGuard.reconcileCompletedCartUpdates();
    kiteCartSynchronizer.refreshGiftEligibility();
    if (this.#scheduledKiteSyncAt && Date.now() >= this.#scheduledKiteSyncAt) {
      this.#scheduledKiteSyncAt = 0;
      this.synchronizeOffer({ isKiteNotification: true });
    }
    if (isCountdownEnabled && !document.hidden) this.renderCountdown();
  }

  renderCountdown() {
    const cartAttributes = this.#currentCart?.attributes || {};
    const storedState = cartAttributes[CART_ATTRIBUTE_KEYS.state];
    const state = storedState === 'active' && Number(cartAttributes[CART_ATTRIBUTE_KEYS.deadline]) <= Date.now() ? 'expired' : storedState;
    const giftItems = this.#currentCart ? this.#getKiteGiftItems(this.#currentCart) : [];
    const isCountdownVisible = isCountdownEnabled && (state === 'expired' || ((state === 'active' || state === 'paused') && giftItems.length));
    const remainingMilliseconds = state === 'paused' ? Number(cartAttributes[CART_ATTRIBUTE_KEYS.remaining]) : Number(cartAttributes[CART_ATTRIBUTE_KEYS.deadline]) - Date.now();
    const remainingSeconds = state === 'expired' ? 0 : Math.max(0, Math.ceil(remainingMilliseconds / 1000));
    const formattedTime = `${String(Math.floor(remainingSeconds / 60)).padStart(2, '0')}:${String(remainingSeconds % 60).padStart(2, '0')}`;
    const giftKeys = new Set(giftItems.map((item) => item.key));

    document.querySelectorAll('cart-gift-countdown').forEach((countdownElement) => {
      const countdownRow = countdownElement.closest('[data-gift-countdown-row]');
      if (!countdownRow?.parentElement) return;
      const firstGiftRow = [...countdownRow.parentElement.querySelectorAll('tr[data-key]')].find((item) => giftKeys.has(item.dataset.key));
      const isCountdownRowVisible = isCountdownVisible && (state === 'expired' || Boolean(firstGiftRow));
      if (countdownElement.hidden === isCountdownRowVisible) countdownElement.hidden = !isCountdownRowVisible;
      if (countdownRow.hidden === isCountdownRowVisible) countdownRow.hidden = !isCountdownRowVisible;
      if (!isCountdownRowVisible) return;
      const visualState = state === 'expired' ? 'expired' : 'active';
      if (countdownElement.dataset.state !== visualState) {
        countdownElement.dataset.state = visualState;
        const countdownMessage = countdownElement.querySelector('[data-gift-countdown-copy]');
        countdownMessage.replaceChildren();
        const messageHeading = document.createElement('strong');
        messageHeading.textContent = visualState === 'expired' ? 'Offer Expired - ' : 'Limited Time Offer - ';
        countdownMessage.append(messageHeading, document.createTextNode(visualState === 'expired'
          ? 'Add another qualifying item to make your free gifts available again.'
          : 'Continue to checkout before time runs out to claim your free gifts.'));
      }
      [...countdownElement.querySelector('[data-gift-countdown-digits]').children].forEach((digit, index) => {
        if (digit.textContent !== formattedTime[index]) digit.textContent = formattedTime[index];
      });
      const timerElement = countdownElement.querySelector('[role="timer"]');
      const timerAccessibleLabel = `Free gift offer time remaining: ${formattedTime}`;
      if (timerElement.getAttribute('aria-label') !== timerAccessibleLabel) timerElement.setAttribute('aria-label', timerAccessibleLabel);
    });
  }
  #handleCheckoutSubmit = (event) => {
    if (!isCountdownEnabled || event.defaultPrevented || !event.submitter?.matches('[name="checkout"]') ||
        !event.target.closest('cart-items-component')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#prepareCheckout(this.#navigateToCheckout);
  };

  #navigateToCheckout = () => location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);

  #handleCheckoutClick = (event) => {
    if (!isCountdownEnabled || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey ||
        event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target : null;
    const checkoutButton = target?.closest('button[name="checkout"]');
    if (checkoutButton && !checkoutButton.disabled && checkoutButton.form?.closest('cart-items-component')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (checkoutButton.form.reportValidity()) this.#prepareCheckout(this.#navigateToCheckout);
      return;
    }
    const link = target?.closest('a[href]');
    if (!link || link.target === '_blank') return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    event.preventDefault();
    this.#prepareCheckout(() => location.assign(url.href));
  };

  async #pauseCountdownForCheckout(signal) {
    signal.throwIfAborted();
    invalidateCartRead();
    const cart = await requestCartResource(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' }, cache: 'no-store', signal,
    });
    signal.throwIfAborted();
    this.#storeCheckoutPause(cart);
    const attributes = cart.attributes || {};
    const state = attributes[CART_ATTRIBUTE_KEYS.state];
    const deadline = Number(attributes[CART_ATTRIBUTE_KEYS.deadline]);
    const hasExpired = state === 'expired' || (state === 'active' && deadline <= Date.now());
    const hasSubscription = this.#getPaidSubscriptionItems(cart).length > 0;
    const shouldRemoveGifts = hasExpired || !hasSubscription;
    const remainingMilliseconds = state === 'paused' ? Number(attributes[CART_ATTRIBUTE_KEYS.remaining]) : deadline - Date.now();
    const nextAttributes = shouldRemoveGifts ? {
      [CART_ATTRIBUTE_KEYS.state]: hasSubscription ? 'expired' : 'inactive',
      [CART_ATTRIBUTE_KEYS.available]: 'false',
      [CART_ATTRIBUTE_KEYS.remaining]: '',
    } : state === 'active' && Number.isFinite(remainingMilliseconds) && remainingMilliseconds > 0 ? {
      [CART_ATTRIBUTE_KEYS.state]: 'paused',
      [CART_ATTRIBUTE_KEYS.remaining]: String(remainingMilliseconds),
    } : {};
    const changedAttributes = Object.fromEntries(Object.entries(nextAttributes).filter(([key, value]) =>
      String(attributes[key] ?? '') !== value
    ));
    const giftUpdates = shouldRemoveGifts ? Object.fromEntries(this.#getKiteGiftItems(cart).map((item) => [item.key, 0])) : {};
    if (!Object.keys(changedAttributes).length && !Object.keys(giftUpdates).length) return;
    const updatedCart = await requestCartResource(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      signal,
      body: JSON.stringify({ attributes: changedAttributes, updates: giftUpdates }),
    });
    signal.throwIfAborted();
    invalidateCartRead();
    this.#currentCart = updatedCart;
    kiteGiftGuard.updateCartAttributes(updatedCart);
    this.#cartBroadcastChannel?.postMessage('cart-update');
  }

  async #prepareCheckout(navigate) {
    if (this.#isCheckoutPending || document.querySelector('[data-cart-update-pending="true"]')) return;
    this.#isCheckoutPending = true;
    const navigationVersion = ++this.#checkoutNavigationVersion;
    this.#storeCheckoutPause(this.#currentCart);
    const controller = new AbortController();
    this.#checkoutAbortController = controller;
    const timeoutSignal = AbortSignal.timeout(CHECKOUT_SYNC_TIMEOUT_MS);
    const abortCheckoutPreparation = () => controller.abort(timeoutSignal.reason);
    timeoutSignal.addEventListener('abort', abortCheckoutPreparation, { once: true });
    let rejectPreparation;
    const preparationAborted = new Promise((resolve, reject) => { rejectPreparation = reject; });
    const handleAbort = () => rejectPreparation(controller.signal.reason);
    controller.signal.addEventListener('abort', handleAbort, { once: true });
    try {
      const pauseCountdown = () => this.#pauseCountdownForCheckout(controller.signal);
      const preparation = navigator.locks
        ? navigator.locks.request('organics-free-gift-cart', { signal: controller.signal }, pauseCountdown)
        : pauseCountdown();
      await Promise.race([preparation, preparationAborted]);
    } catch (error) {
      if (navigationVersion === this.#checkoutNavigationVersion) {
        console.warn('[cart-gift-countdown] Checkout will continue without a confirmed server pause.', error);
      }
    } finally {
      timeoutSignal.removeEventListener('abort', abortCheckoutPreparation);
      controller.signal.removeEventListener('abort', handleAbort);
      if (this.#checkoutAbortController === controller) this.#checkoutAbortController = null;
    }
    if (navigationVersion !== this.#checkoutNavigationVersion) return;
    try {
      navigate();
    } catch (error) {
      this.#isCheckoutPending = false;
      this.#resumeStoredCountdown();
      console.error('[cart-gift-countdown] Checkout navigation failed.', error);
    }
  }
}

class KiteCartSync {
  #hasPendingRefresh = false;
  #isRefreshing = false;
  #readinessAttemptCount = 0;
  #lastPaidItemsSignature = null;
  #forcedRefreshVersion = 0;

  constructor() {
    if (!isCountdownEnabled) return;
    document.addEventListener(ThemeEvents.cartUpdate, (event) => {
      if (event.detail?.data?.didError ||
          !['product-form-component', 'cart-bundle-component', 'cart-items-component'].includes(event.detail?.data?.source)) return;
      this.scheduleEligibilityRefresh();
    });
    document.addEventListener('gfg:module:loaded', () => this.refreshGiftEligibility());
    this.scheduleEligibilityRefresh();
  }

  scheduleEligibilityRefresh(forceRefresh = false) {
    if (!isCountdownEnabled) return;
    if (forceRefresh) {
      this.#forcedRefreshVersion++;
      this.#lastPaidItemsSignature = null;
    }
    if (this.#hasPendingRefresh) return;
    this.#hasPendingRefresh = true;
    this.#readinessAttemptCount = 0;
    queueMicrotask(() => this.refreshGiftEligibility());
  }

  hasCurrentEligibility(cart) {
    return getPaidItemsSignature(cart) === this.#lastPaidItemsSignature;
  }

  async refreshGiftEligibility(currentCart = null) {
    if (!this.#hasPendingRefresh || this.#isRefreshing || giftCountdownManager.isProcessingSynchronization()) return;
    const kiteGiftEngine = kiteGiftRules.getEngine();
    const kiteUtility = window.gfg?.utility;
    if (!kiteGiftEngine || kiteGiftEngine._isStub || typeof kiteGiftEngine._processCartChange !== 'function' ||
        !kiteGiftEngine.state?.campaigns?.length || typeof kiteUtility?.updateCache !== 'function' || kiteGiftEngine.state.isProcessing) {
      if (++this.#readinessAttemptCount >= 12) this.#hasPendingRefresh = false;
      return;
    }

    this.#isRefreshing = true;
    this.#hasPendingRefresh = false;
    const forcedRefreshVersion = this.#forcedRefreshVersion;
    try {
      const cart = currentCart && kiteGiftGuard.canReuseCart(currentCart) ? currentCart : await readCart();
      const paidItemsSignature = getPaidItemsSignature(cart);
      if (paidItemsSignature === this.#lastPaidItemsSignature ||
          (kiteGiftGuard.shouldBlockGiftAddition(cart) && !giftCountdownManager.hasPendingQualifyingAddition())) return;
      if (kiteGiftEngine.state.isProcessing || giftCountdownManager.isProcessingSynchronization()) {
        this.#hasPendingRefresh = true;
        return;
      }
      kiteUtility.updateCache(cart);
      kiteGiftEngine.state.lastCartHash = null;
      await kiteGiftEngine._processCartChange();
      if (forcedRefreshVersion === this.#forcedRefreshVersion) this.#lastPaidItemsSignature = paidItemsSignature;
      this.#readinessAttemptCount = 0;
      kiteGiftGuard.scheduleCartReconciliation();
    } catch (error) {
      console.error('[kite-cart-sync]', error);
    } finally {
      this.#isRefreshing = false;
      if (this.#hasPendingRefresh) queueMicrotask(() => this.refreshGiftEligibility());
    }
  }
}

const kiteGiftRules = new KiteGiftRules();
const kiteGiftGuard = new KiteGiftGuard();
kiteGiftGuard.installGiftGuards();
document.addEventListener('gfg:module:loaded', () => kiteGiftGuard.installGiftGuards());
const kiteCartSynchronizer = new KiteCartSync();
const giftCountdownManager = new CartGiftCountdownManager();
class CartGiftCountdown extends HTMLElement {
  connectedCallback() {
    requestAnimationFrame(() => { if (this.isConnected) giftCountdownManager.renderCountdown(); });
  }
}
if (!customElements.get('cart-gift-countdown')) customElements.define('cart-gift-countdown', CartGiftCountdown);
